<template>
  <div
    ref="dropZone"
    class="cursor-pointer border-2 border-gray-400 border-dashed flex flex-col justify-center items-center w-full h-full rounded p-5"
    :class="{'bg-gray-400': isOverDropZone}"
    @click="fileInput.click()"
  >
    <button
      type="button"
      class="flex flex-col space-y-2 items-center "
      :class="[isOverDropZone ? 'text-white' : 'text-ccm-blue-500']"
    >
      <div
        v-if="uploading"
        class="flex justify-center"
      >
        <AppSpinner />
      </div>

      <div
        v-else
        class="flex flex-col text-sm md:text-base font-medium items-center space-y-0.5"
      >
        <div class="flex space-x-2 items-center">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 ">
    <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
  </svg>

          <span>
            {{ $t('FileUpload.uploadFile') }}
          </span>
        </div>

        <span
          class="text-xs"
          :class="[isOverDropZone ? 'text-white' : 'text-gray-700']"
        >{{ $t('FileUpload.dragAndDrop') }}</span>
      </div>

      <span
        v-if="error"
        class="text-red-600 text-sm block"
      >{{ error }}</span>
    </button>

    <input
      ref="fileInput"
      hidden
      name="images"
      type="file"
      v-bind="$attrs"
      @change="fileUploaded"
    >
  </div>
</template>

<script setup lang="ts">
import { useDropZone } from '@vueuse/core';
import { useAttrs, watch } from 'vue';
import { ref, useTemplateRef } from 'vue';

const props = withDefaults(
  defineProps<{
    // name: string;
    // modelValue: string | number | null | undefined;
    error?: string;
    resetOnInput?: boolean;
    uploading?: boolean;
  }>(),
  {
    error: undefined,
    resetOnInput: false,
    uploading: false,
  },
);

const attrs = useAttrs();

defineOptions({
  inheritAttrs: false,
});

// TODO: 14/03/25 enable 2 way sync when removing file
const filesUploaded = defineModel<File[]>();

const fileUploaded = (event: Event) => {
  const files = (event.target as HTMLInputElement).files;

  if(props.resetOnInput) {
    filesUploaded.value = Array.from(files);
  } else if(filesUploaded.value) {
    filesUploaded.value = filesUploaded.value.concat(Array.from(files));
  } else {
    filesUploaded.value = Array.from(files);
  }

  /* eslint-disable no-null/no-null */
  fileInput.value.value = null;
};

const fileInput = useTemplateRef('fileInput');

watch(filesUploaded, (newValue) => {
  if(newValue?.length === 0) {
    /* eslint-disable no-null/no-null */
    fileInput.value.value = null;
  }
});

const resetInput = () => {
  /* eslint-disable no-null/no-null */
  fileInput.value.value = null;
  filesUploaded.value = [];
};

defineExpose({
  resetInput,
});

const dropZoneRef = useTemplateRef('dropZone');

const onDrop = (files: File[] | undefined) => {
  if(!files || files.length === 0) {
    return;
  }

  const dataTransfer = new DataTransfer();

  for(const file of files) {
    dataTransfer.items.add(file);
  }

  fileInput.value.files = dataTransfer.files;
  fileInput.value.dispatchEvent(new Event('change', { bubbles: true }));
};

const { isOverDropZone } = useDropZone(dropZoneRef, {
  onDrop,
  // specify the types of data to be received.
  dataTypes: attrs.accept ? (attrs.accept as string | undefined)?.split(',') : undefined,
  // control multi-file drop
  multiple: true,
  // whether to prevent default behavior for unhandled events
  preventDefaultForUnhandled: false,
});
</script>

<style scoped></style>
